import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AgGrid from "../../../UI/AgGrid";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { goodsReceiveColumnDefs } from "./columnDefs";
import { GOODS_RECEIVE_VIEW } from "../../../../services/AgGrid/InventoryAggrid";
import { AgGridReact } from "ag-grid-react";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { IGoodsReceive } from "../../../../types/Inventory/GoodsReceive";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";

interface Props {
  gridRef: RefObject<AgGridReact<IGoodsReceive>>;
  isFilter?: string | null;
  setRowCount?: Dispatch<SetStateAction<number>>;
  referenceUniqueId?: string;
}

const GoodsReceiveTable = ({
  gridRef,
  isFilter,
  setRowCount,
  referenceUniqueId,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnDef: ColDef[] = goodsReceiveColumnDefs(t, referenceUniqueId);

  const graphQLClientWithHeaderItem: GraphQLClient =
    createGraphQLClientWithMiddleware("wms");

  const datasource: IServerSideDatasource = {
    async getRows(params) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      const { created_date, posted_date, reference_unique_id_list } =
        filterModel;

      const formatFilter = {
        ...filterModel,
        created_date: dateFilterModel(created_date),
        posted_date: dateFilterModel(posted_date),
        reference_unique_id_list: referenceUniqueId
          ? {
              filterType: "array",
              type: "hasSome",
              values: [referenceUniqueId],
            }
          : reference_unique_id_list
          ? {
              filterType: "array",
              type: "hasSome",
              values: [reference_unique_id_list.filter],
            }
          : undefined,
      };

      try {
        const { GoodsReceivesView } = await graphQLClientWithHeaderItem.request(
          GOODS_RECEIVE_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          }
        );
        if (setRowCount) {
          setRowCount(GoodsReceivesView.count as number);
        }

        params.success({
          rowData: GoodsReceivesView.results as any[],
          rowCount: GoodsReceivesView.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("aggrid_status");
      const reference = params.api.getFilterInstance("reference_unique_id");

      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "finished":
          instance?.setModel({ values: ["finished"] });
          break;
        case "wait_receive":
          instance?.setModel({ values: ["wait_receive"] });
          break;
        case "in_transit":
          instance?.setModel({ values: ["in_transit"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          if (referenceUniqueId) instance?.setModel({});
          else
            instance?.setModel({
              values: ["draft", "finished", "wait_receive", "in_transit"],
            });
          break;
      }
      reference?.setModel({
        filterType: "text",
        type: "contains",
        filter: referenceUniqueId,
      });
      params.api.onFilterChanged();
    },
    [isFilter, referenceUniqueId]
  );

  const onGridReady = (params: GridReadyEvent) => {
    const branch_id = sessionStorage.getItem("branch-id");
    const is_hq = sessionStorage.getItem("is_hq");

    const instanceฺฺBranchId = params.api.getFilterInstance("branch_id");
    instanceฺฺBranchId?.setModel({
      filterType: "number",
      type: "equals",
      filter: is_hq === "false" && branch_id ? parseInt(branch_id) : undefined,
    });
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    navigate(`/inventory/goods-receive/${encodeURIComponent(params.data.id)}`);
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onGridReady={onGridReady}
      path={"/inventory/goods_receive"}
    />
  );
};

export default GoodsReceiveTable;
